import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BorderBeam } from 'border-beam';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles, TriangleAlert, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { PageContainer } from '../../../../common/components/PageContainer';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsBrandKit } from '../../../../common/models/content-studio.model';
import { VendorProfile, useVendorProfileApi } from '../../../../common/hooks/use-vendor-profile-api.hook';
import { BrandOrbit } from '../../../../modules/vendor/brand/BrandOrbit';
import { BrandFieldKey, buildBrandNodes, hasAnything } from '../../../../modules/vendor/brand/brand-nodes';
import { brandCompleteness, socialReadPending } from '../../../../modules/vendor/brand/brand-completeness';
import { useSocialSchedulerApi } from '../../../../common/hooks/use-social-scheduler-api.hook';
import { SocialAccount } from '../../../../common/models/social-scheduler.model';
import { useAutosaveReporterState } from '../../../../common/hooks/use-autosave.hook';
import { AutosaveIndicator } from '../../../../components/design-system/common/AutosaveIndicator';

/**
 * Your brand, as a thing rather than a form.
 *
 * The kit is read from what is actually there — palette from their CSS, font
 * from their stylesheets, tone from their copy, visual style from their
 * photography — with the evidence beside each field, and all of it editable,
 * because the reading can be wrong and because a brand can be decided here as
 * much as discovered.
 *
 * It used to present that as a stack of labelled cards, which is a faithful
 * rendering of the data model and a poor rendering of a brand: a colour in a
 * text input is a fact about a database, while the same colour as a disc among
 * the photographs it was taken from is the thing itself. So the page is the
 * sphere, everything the kit knows orbits the brand's own name, and clicking
 * any of it opens that one field with its evidence and nothing else.
 *
 * Why it matters: the planner reads this kit on every turn. What is written
 * here is what "on-brand" means to it.
 */

type FieldKey = 'brandName' | 'primaryColors' | 'secondaryColors' | 'primaryFont'
    | 'toneOfVoice' | 'styleKeywords' | 'visualStyle' | 'bannedWords';

interface DiscoveryReport {
    pagesRead: string[];
    palette: Array<{ hex: string; weight: number; role: string }>;
    fonts: Array<{ family: string; weight: number; loaded: boolean }>;
    profiles: Array<{ platform: string; handle: string; bio?: string; followers?: number; avatarUrl?: string }>;
    limits: string[];
}

/** What each field is called, and what to say when it holds nothing yet. */
const FIELD_TITLE: Record<BrandFieldKey, [key: string, fallback: string]> = {
    brandName: ['BRAND.NAME', 'Brand name'],
    primaryColors: ['BRAND.COLOURS', 'Brand colours'],
    primaryFont: ['BRAND.FONT', 'Typeface'],
    toneOfVoice: ['BRAND.TONE', 'How you write'],
    styleKeywords: ['BRAND.STYLE', 'Your visual world'],
    visualStyle: ['BRAND.VISUAL_STYLE', 'How you show yourself in pictures'],
    bannedWords: ['BRAND.BANNED', 'Words to avoid'],
    referenceImageUrls: ['BRAND.REFERENCES', 'Reference pictures'],
    competitors: ['BRAND.COMPETITORS', 'Competitors'],
    markets: ['BRAND.MARKETS', 'Markets'],
    houseRules: ['BRAND.HOUSE_RULES', 'House rules'],
};

const FIELD_EMPTY: Record<BrandFieldKey, [key: string, fallback: string]> = {
    brandName: ['BRAND.EMPTY.NAME', 'Name your brand'],
    primaryColors: ['BRAND.EMPTY.COLOURS', 'Add your colours'],
    primaryFont: ['BRAND.EMPTY.FONT', 'Add your typeface'],
    toneOfVoice: ['BRAND.EMPTY.TONE', 'How do you write?'],
    styleKeywords: ['BRAND.EMPTY.STYLE', 'Describe your visual world'],
    visualStyle: ['BRAND.EMPTY.VISUAL_STYLE', 'How do your pictures look?'],
    bannedWords: ['BRAND.EMPTY.BANNED', 'Words to avoid'],
    referenceImageUrls: ['BRAND.EMPTY.REFERENCES', 'Add reference pictures'],
    competitors: ['BRAND.EMPTY.COMPETITORS', 'Who do you compete with?'],
    markets: ['BRAND.EMPTY.MARKETS', 'Where do you sell?'],
    houseRules: ['BRAND.EMPTY.HOUSE_RULES', 'Add your house rules'],
};

const BrandPage: React.FC = () => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const profileApi = useVendorProfileApi();
    const socialApi = useSocialSchedulerApi();
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();

    const [kit, setKit] = useState<CsBrandKit | null>(null);
    /**
     * The business profile is the source for the website and the profiles:
     * discovery reads from it and writes what the vendor typed back to it,
     * so the dashboard's setup gaps and this page never disagree.
     */
    const [profile, setProfile] = useState<VendorProfile>({});
    const [savingProfile, setSavingProfile] = useState<'competitors' | 'markets' | 'houseRules' | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<FieldKey | 'referenceImageUrls' | 'logoUrl' | null>(null);

    /** The field whose panel is open. One at a time, by design. */
    /**
     * What the side panel is showing: one field, or the reading form.
     *
     * The reading form used to be a sheet sliding over the page — a second
     * kind of surface for the same job, opening in a different place with a
     * different width, over the sphere it is about. The panel is already where
     * this page puts a thing you are working on, so it goes there too.
     */
    const [selected, setSelected] = useState<BrandFieldKey | 'discover' | null>(null);
    /**
     * Platforms with a live connection.
     *
     * Needed here rather than inferred from `socialHandles`: a handle on file
     * means we know their username, which is what a crawl reads. A connection
     * is what lets us read their captions, and those are two very different
     * amounts of knowing.
     */
    const [accounts, setAccounts] = useState<SocialAccount[]>([]);
    const connected = useMemo(() => [...new Set(accounts.map((a) => a.platform))], [accounts]);

    /*
     * An account connected since the kit was last read.
     *
     * The bar counts the connection as satisfied the moment it exists, which
     * is right — but it can then read fully complete while the kit has never
     * seen one of their captions. The evidence is sitting there unread, so the
     * page says so and offers the re-read rather than quietly spending on one.
     */
    const socialUnread = useMemo(() => socialReadPending(kit, accounts), [kit, accounts]);
    /** A narrow re-look over the reference pictures, not a whole re-read. */
    const [looking, setLooking] = useState(false);

    // ── Discovery ────────────────────────────────────────────────
    const [website, setWebsite] = useState('');
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState<string[]>([]);
    const [report, setReport] = useState<DiscoveryReport | null>(null);
    const [suggestions, setSuggestions] = useState<Record<string, any>>({});
    const [error, setError] = useState<string | null>(null);
    const streamRef = useRef<AbortController | null>(null);

    /**
     * Fields the vendor has typed into by hand since discovery last ran.
     *
     * Discovery does not overwrite these; it reports what it found for them
     * as a suggestion instead. Otherwise re-running it to pick up a new
     * Instagram handle would stamp on the tone of voice they had just fixed.
     */
    const [touched, setTouched] = useState<Set<string>>(new Set());

    const load = useCallback(async () => {
        try {
            const [k, p, accounts] = await Promise.all([
                api.getBrandKit(),
                profileApi.get().catch(() => ({} as VendorProfile)),
                socialApi.listAccounts({ status: 'active', limit: 20 }).catch(() => ({ items: [] as SocialAccount[] })),
            ]);
            setKit(k);
            setProfile(p);
            setAccounts(accounts.items ?? []);
            // What is on file wins; what discovery last read is the fallback.
            const site = p.websiteUrl || (k.lastExtractedFrom && /^https?:/.test(k.lastExtractedFrom) ? k.lastExtractedFrom : '');
            if (!website && site) setWebsite(site);

        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { void load(); return () => streamRef.current?.abort(); }, [load]);

    /**
     * `socialOnly` reads just the connected accounts.
     *
     * Offered when the only new evidence is an account connected since the
     * last read: the website and the homepage screenshot were read already and
     * have not changed, so re-reading them is time and credits spent on
     * answers we have.
     */
    const discover = useCallback((socialOnly = false) => {
        // A website, or an account we can actually read. Handles are no longer
        // asked for here — see the sheet.
        if (!website.trim() && accounts.length === 0) return;
        setRunning(true);
        setProgress([]);
        setReport(null);
        setSuggestions({});
        setError(null);
        streamRef.current?.abort();

        // The form is also the profile, for the one field it still asks for.
        // Handles are not written from here any more: the accounts panel is
        // the truth about socials, and typing a username over a connection
        // would leave two answers to the same question.
        if (website.trim()) {
            profileApi.update({ websiteUrl: website.trim() }).then(setProfile).catch(() => undefined);
        }

        streamRef.current = api.discoverBrandStream(
            {
                websiteUrl: socialOnly ? undefined : website.trim() || undefined,
                keep: [...touched],
                ...(socialOnly ? { only: 'social' as const } : {}),
            },
            (event) => {
                if (event.type === 'thinking' && event.data?.message) {
                    setProgress((prev) => [...prev, String(event.data.message)]);
                } else if (event.type === 'tool_result' && event.data?.name === 'brand_kit_updated') {
                    setKit(event.data.brandKit);
                    setReport(event.data.report || null);
                    setSuggestions(event.data.suggestions || {});
                } else if (event.type === 'error') {
                    setError(event.data?.message || t('BRAND.DISCOVERY_FAILED', 'Discovery could not finish.'));
                    setRunning(false);
                } else if (event.type === 'done') {
                    setRunning(false);
                }
            },
        );
    }, [api, profileApi, website, accounts.length, touched, t]);

    /**
     * Re-read the visual style from the pictures as they are now.
     *
     * Offered where the staleness is, and only when there is staleness: the
     * alternative is "Read again", which re-fetches the website and re-bills
     * for it to repair one derived field.
     */
    const lookAgain = useCallback(() => {
        setLooking(true);
        streamRef.current?.abort();
        streamRef.current = api.lookAgainStream((event) => {
            if (event.type === 'tool_result' && event.data?.name === 'brand_kit_updated') {
                setKit(event.data.brandKit);
            } else if (event.type === 'error') {
                setError(event.data?.message || '');
                setLooking(false);
                /*
                 * A refusal here still changes the kit: when nothing in the
                 * pictures is a room, the stale description is cleared rather
                 * than left sitting there being read to the planner. So the
                 * panel has to catch up, or it keeps showing text the server
                 * has already thrown away.
                 */
                void api.getBrandKit().then(setKit).catch(() => undefined);
            } else if (event.type === 'done') {
                setLooking(false);
            }
        });
    }, [api]);

    // ── Editing ──────────────────────────────────────────────────
    /*
     * Every field saves as it is left, so there is no Save button; the top bar
     * says when a save is running, done, or failed — with a Retry of that save.
     */
    const autosave = useAutosaveReporterState();
    const tracked = useCallback(async (run: () => Promise<void>) => {
        autosave.reporter.report('saving', () => { void tracked(run); });
        try {
            await run();
            autosave.reporter.report('saved', () => {});
        } catch (error) {
            console.error('Brand save failed:', error);
            autosave.reporter.report('error', () => { void tracked(run); });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = useCallback((patch: Partial<CsBrandKit>, key: FieldKey | 'referenceImageUrls' | 'logoUrl') => tracked(async () => {
        setSaving(key);
        try {
            const updated = await api.updateBrandKit(patch as any);
            setKit(updated);
            setTouched((prev) => new Set(prev).add(key));
        } finally {
            setSaving(null);
        }
    }), [api, tracked]);

    const saveProfile = useCallback((patch: Partial<VendorProfile>, key: 'competitors' | 'markets' | 'houseRules') => tracked(async () => {
        setSavingProfile(key);
        try {
            setProfile(await profileApi.update(patch));
        } finally {
            setSavingProfile(null);
        }
    }), [profileApi, tracked]);
    const statusIndicator = <AutosaveIndicator status={autosave.status} onRetry={autosave.retry} />;

    const nodes = useMemo(
        () => buildBrandNodes(kit, profile, { empty: (f) => t(...FIELD_EMPTY[f]) as string }),
        [kit, profile, t],
    );
    /**
     * Whether the sphere has anything in it at all — the hint above it.
     *
     * Nodes rather than the kit, because a competitor the vendor typed is
     * genuinely something to turn around and look at.
     */
    const known = hasAnything(nodes);

    /**
     * Whether the brand has ever actually been READ, which is a different
     * question and the one the button is answering.
     *
     * It used to ask `known`, so a single competitor URL on the business
     * profile — nothing to do with discovery — was enough to turn "Read my
     * brand" into "Read again" on an account whose kit was empty. The kit
     * already records when it was last extracted; that is the fact, rather
     * than a proxy for it.
     */
    const everRead = !!kit?.lastExtractedAt;
    const completeness = useMemo(
        () => brandCompleteness(kit, profile, connected, (key) => t(key) as string),
        [kit, profile, connected, t],
    );

    const title = (field: BrandFieldKey) => t(...FIELD_TITLE[field]) as string;

    if (loading) {
        return (
            <PageContainer breadcrumbs={[{ label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') }, { label: t('BRAND.TITLE', 'Your brand') }]}>
                <div className="flex justify-center py-20">
                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            breadcrumbs={[{ label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') }, { label: t('BRAND.TITLE', 'Your brand') }]}
            status={statusIndicator}
        >
            <div className="flex h-[calc(100vh-200px)] min-h-[540px] gap-4">

                {/* ── The brand, turned around in your hands ────────── */}
                {/*
                  * `isolate` gives the orbit its own stacking context.
                  *
                  * Without it, `relative` alone leaves the header and the nodes
                  * in the root context, where their z-indexes are compared
                  * against the sheet's — and a node at 101 wins against an
                  * overlay at 50, so the brand kept floating over its own
                  * dialog. Scoped here, the numbers below only ever order the
                  * orbit against itself.
                  */}
                <section className="relative isolate flex-1 overflow-hidden rounded-xl border bg-card">
                    <header className="absolute inset-x-0 top-0 z-50 flex items-start justify-between gap-4 p-5">
                        <div>
                            <p className="font-heading text-sm font-medium tracking-tight text-foreground">
                                {t('BRAND.TITLE', 'Your brand')}
                            </p>
                            <p className="mt-0.5 max-w-[420px] text-xs/relaxed text-muted-foreground">
                                {known
                                    ? t('BRAND.ORBIT_HINT', 'Everything we know about you. Drag to turn it, click anything to change it.')
                                    : t('BRAND.ORBIT_EMPTY', 'Nothing has been read yet. Give us your website and we will work this out.')}
                            </p>

                            {/*
                              * How much of the brand we actually know.
                              *
                              * The kit used to be all-or-nothing: a vendor whose
                              * run found a palette and nothing else saw the same
                              * finished-looking page as one we knew inside out,
                              * and the only clue was that the agent's output came
                              * back generic. The figure is the smaller half of
                              * this — the list under it is the instruction.
                              */}
                            <div className="mt-4 max-w-[420px]">
                                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                                    <span className="text-xs font-medium text-foreground">
                                        {t('BRAND.KNOWN', { percent: completeness.percent })}
                                    </span>
                                    {everRead && completeness.gaps.length > 0 && (
                                        <span className="text-[11px] text-muted-foreground">
                                            {t('BRAND.GAPS_TITLE', 'What would help most')}
                                        </span>
                                    )}
                                </div>
                                <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                                    <div
                                        className="h-full rounded-full bg-foreground transition-[width] duration-500"
                                        style={{ width: `${completeness.percent}%` }}
                                    />
                                </div>

                                {/* The three that would move it most, as things to
                                    press rather than a list to read. */}
                                {/* Held back until something has been read. At
                                    zero every gap is open, so the list is just
                                    the whole kit restated — and the one useful
                                    action is the button below, not eleven. */}
                                <div className="mt-2.5 flex flex-wrap gap-1.5">
                                    {(everRead ? completeness.gaps.slice(0, 3) : []).map((gap) => (
                                        <button
                                            key={gap.key}
                                            type="button"
                                            data-testid={`brand-gap-${gap.key}`}
                                            onClick={() => {
                                                /*
                                                 * A connection cannot be typed, so it goes where
                                                 * it can actually be made — the accounts tab, not
                                                 * the calendar the scheduler opens on. Landing a
                                                 * vendor on next week's posts when they came to
                                                 * connect an account makes them hunt for it.
                                                 */
                                                if (gap.kind === 'connection') navigate('/marketing/social-scheduler?tab=accounts');
                                                else if (gap.field) setSelected(gap.field);
                                            }}
                                            className="rounded-full border border-dashed px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                                        >
                                            {gap.kind === 'connection'
                                                ? `${t('BRAND.CONNECT', 'Connect')} — ${gap.label}`
                                                : gap.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* Only once there is something to read again. Before
                            that the one thing to do is the button below, and a
                            second copy of it in the corner splits the ask. */}
                        {everRead && (
                            <Button
                                variant="outline"
                                onClick={() => setSelected('discover')}
                                // Marked when there is unread evidence, so the
                                // button says which of the two things it is.
                                className={cn(socialUnread && 'border-foreground')}
                            >
                                <Sparkles data-icon="inline-start" />
                                {socialUnread
                                    ? t('BRAND.READ_SOCIAL_NOW', 'Read your posts')
                                    : t('BRAND.READ_AGAIN', 'Read again')}
                            </Button>
                        )}
                    </header>

                    <BrandOrbit
                        nodes={nodes}
                        centreLabel={kit?.brandName || t('BRAND.YOUR_BRAND', 'Your brand') as string}
                        centreImageUrl={kit?.logoUrl}
                        activeField={selected === 'discover' ? null : selected}
                        coreColors={[...(kit?.primaryColors ?? []), ...(kit?.secondaryColors ?? [])]}
                        onSelect={(node) => setSelected(node.field)}
                        onSelectCentre={() => setSelected('brandName')}
                        className="h-full w-full"
                    />

                    {/*
                      * Nothing read yet, so there is exactly one thing to do.
                      *
                      * Bottom centre and beamed rather than a quiet outline in
                      * the corner: an empty sphere is a page with nothing on it
                      * to press, and the vendor's question at that moment is
                      * "what now" rather than "what else". The same travelling
                      * beam the studio's Generate button and the credits block
                      * wear, which is what it means here too — this is ready,
                      * press it.
                      */}
                    {!everRead && !running && (
                        <div className="absolute inset-x-0 bottom-10 z-50 flex justify-center">
                            {/*
                              * Outlined, and the beam turned down.
                              *
                              * Filled black with a fast beam on it read as an
                              * alarm rather than an invitation: it is the only
                              * lit object on a pale page and it pulled the eye
                              * off the sphere the page is about. Outlined is
                              * what this product uses for a primary action
                              * anyway, and the beam is doing the work of
                              * marking it — it does not also need the weight.
                              */}
                            <BorderBeam size="md" colorVariant="ocean" duration={4} strength={0.22} active={!reduceMotion}>
                                <Button variant="outline" size="lg" className="bg-background px-6" onClick={() => setSelected('discover')}>
                                    <Sparkles data-icon="inline-start" />
                                    {t('BRAND.READ_CTA', 'Read my brand from my website')}
                                </Button>
                            </BorderBeam>
                        </div>
                    )}

                    {/* What it is doing, over the thing it is changing. */}
                    {(running || error) && (
                        <div className="absolute inset-x-0 bottom-0 z-50 flex flex-col gap-1 bg-gradient-to-t from-card via-card/95 to-transparent p-5 pt-10">
                            {progress.slice(-3).map((line, i, shown) => (
                                // Positional: progress is a log, not a keyed set.
                                // eslint-disable-next-line react/no-array-index-key
                                <p key={i} className={cn('text-xs', i === shown.length - 1 ? 'text-foreground' : 'text-muted-foreground')}>
                                    {line}
                                </p>
                            ))}
                            {error && <p className="text-xs text-destructive">{error}</p>}
                        </div>
                    )}
                </section>

                {/* ── One field, with its evidence and nothing else ─── */}
                {selected && (
                    <aside
                        data-testid="brand-field-panel"
                        className="flex w-[380px] shrink-0 flex-col overflow-hidden rounded-xl border bg-card"
                    >
                        <div className="flex items-center justify-between gap-3 border-b px-5 py-3.5">
                            <p className="font-heading text-sm font-medium tracking-tight text-foreground">
                                {selected === 'discover'
                                    ? (everRead
                                        ? t('BRAND.DISCOVER_AGAIN_TITLE', 'Read your brand again')
                                        : t('BRAND.DISCOVER_TITLE', 'Let’s work out your brand'))
                                    : title(selected)}
                            </p>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 text-muted-foreground"
                                aria-label={t('COMMON.CLOSE', 'Close') as string}
                                onClick={() => setSelected(null)}
                            >
                                <X className="size-4" />
                            </Button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5">
                            {selected === 'discover' && (
                                <>
                                    <p className="mb-4 text-xs/relaxed text-muted-foreground">
                                        {t('BRAND.DISCOVER_HINT',
                                            'Give us your website and any profiles you post from. We read them — your colours from your stylesheets, your font, how you write, what your photography looks like — and show you where each thing came from. You can change any of it.')}
                                    </p>
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-1.5">
                                            <Label htmlFor="brand-website" className="text-muted-foreground">
                                                {t('BRAND.WEBSITE', 'Website')}
                                            </Label>
                                            <Input
                                                id="brand-website" placeholder="https://your-site.com" value={website}
                                                onChange={(e) => setWebsite(e.target.value)} disabled={running}
                                            />
                                        </div>
                                        {/*
                                          * The accounts we can actually read, not usernames.
                                          *
                                          * A typed handle buys a public profile fetch, which
                                          * returns what a stranger gets: a name, a bio, and no
                                          * posts, because every platform that matters renders
                                          * its feed from a script. A connected account hands
                                          * back their own captions — the best evidence of how a
                                          * brand writes that exists. Asking for the username
                                          * offered the worse of the two as though it were a
                                          * choice.
                                          */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-muted-foreground">
                                                {t('BRAND.SOCIAL_ACCOUNTS', 'Social accounts')}
                                            </Label>

                                            {accounts.length > 0 ? (
                                                <>
                                                    {accounts.map((account) => (
                                                        <div
                                                            key={account.uuid}
                                                            className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2"
                                                        >
                                                            {account.accountAvatar
                                                                ? <img src={account.accountAvatar} alt="" className="size-6 shrink-0 rounded-full object-cover" />
                                                                : <span className="size-6 shrink-0 rounded-full bg-muted" />}
                                                            <span className="truncate text-sm text-foreground">{account.accountName}</span>
                                                            <span className="ms-auto shrink-0 text-[11px] text-muted-foreground capitalize">
                                                                {account.platform}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <p className="text-xs/relaxed text-muted-foreground">
                                                        {t('BRAND.SOCIAL_CONNECTED_HINT', 'We read your own posts from these — their captions are the best evidence of how you write.')}
                                                    </p>
                                            </>
                                        ) : (
                                            <div className="rounded-lg border border-dashed px-3 py-3">
                                                <p className="mb-2.5 text-xs/relaxed text-muted-foreground">
                                                    {t('BRAND.SOCIAL_NONE_HINT', 'No account is connected. Connecting one lets us read your captions, which tell us more about how you write than a website ever does.')}
                                                </p>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate('/marketing/social-scheduler?tab=accounts')}
                                                >
                                                    {t('BRAND.CONNECT_ACCOUNT', 'Connect an account')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-4">
                                        {/* Called through an arrow: passing `discover` straight to
                                            onClick would hand it the click event, which is truthy
                                            and would silently run every read as social-only. */}
                                        <Button onClick={() => discover()} disabled={running || (!website.trim() && accounts.length === 0)}>
                                            {running
                                                ? t('BRAND.READING', 'Reading…')
                                                : everRead ? t('BRAND.READ_AGAIN', 'Read again') : t('BRAND.READ', 'Read my brand')}
                                        </Button>

                                        {/* The narrow read, offered only where the narrowness is
                                            true: an account connected since the last read, and a
                                            kit that has therefore never seen a caption. */}
                                        {socialUnread && !running && (
                                            <Button variant="outline" onClick={() => discover(true)}>
                                                {t('BRAND.READ_SOCIAL_ONLY', 'Read only my posts')}
                                            </Button>
                                        )}
                                        {touched.size > 0 && (
                                            <p className="text-xs text-muted-foreground">
                                                {t('BRAND.KEEPING_EDITS', 'Your edits are kept — anything found for those fields is offered as a suggestion.')}
                                            </p>
                                        )}
                                    </div>

                                    {/* What was read, and what could not be. Said plainly, so a
                                        thin kit reads as thin rather than complete. */}
                                    {report && (
                                        <div className="flex flex-col gap-2.5 border-t pt-4">
                                            <p className="text-xs text-muted-foreground">
                                                {t('BRAND.READ_SUMMARY', {
                                                    defaultValue: 'Read {{pages}} page(s). {{colours}} colours and {{fonts}} fonts were readable from the stylesheets.',
                                                    pages: report.pagesRead.length, colours: report.palette.length, fonts: report.fonts.length,
                                                })}
                                            </p>
                                            {report.palette.length > 0 && (
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className="me-0.5 text-xs text-muted-foreground">{t('BRAND.PALETTE_SEEN', 'Palette in your CSS:')}</span>
                                                    {report.palette.slice(0, 10).map((c) => (
                                                        <Swatch key={c.hex} hex={c.hex} round tooltip={`${c.hex} · ${c.weight} uses · ${c.role}`} />
                                                    ))}
                                                </div>
                                            )}
                                            {report.profiles.map((p) => (
                                                <div key={`${p.platform}-${p.handle}`} className="flex items-center gap-2">
                                                    {p.avatarUrl && <img src={p.avatarUrl} alt="" className="size-6 rounded-full object-cover" />}
                                                    <p className="text-xs text-muted-foreground">
                                                        <strong className="font-medium text-foreground">{p.platform} · @{p.handle}</strong>
                                                        {p.followers ? ` · ${p.followers.toLocaleString()} ${t('BRAND.FOLLOWERS', 'followers')}` : ''}
                                                        {p.bio ? ` — ${p.bio}` : ''}
                                                    </p>
                                                </div>
                                            ))}
                                            {report.limits.length > 0 && <Limits limits={report.limits} />}
                                        </div>
                                    )}
                                </div>
                                </>
                            )}

                            {selected === 'brandName' && (
                                <TextFieldCard
                                    bare title={title('brandName')} value={kit?.brandName || ''}
                                    evidence={kit?.evidence?.brandName} suggestion={suggestions.brandName}
                                    saving={saving === 'brandName'}
                                    onSave={(v) => save({ brandName: v }, 'brandName')}
                                />
                            )}
                            {selected === 'primaryFont' && (
                                <TextFieldCard
                                    bare title={title('primaryFont')} value={kit?.primaryFont || ''}
                                    evidence={kit?.evidence?.primaryFont} suggestion={suggestions.primaryFont}
                                    hint={t('BRAND.FONT_HINT', 'Designs the agent composes render in this by default.')}
                                    saving={saving === 'primaryFont'}
                                    onSave={(v) => save({ primaryFont: v }, 'primaryFont')}
                                />
                            )}
                            {selected === 'primaryColors' && (
                                <ColorsCard
                                    bare title={title('primaryColors')} colors={kit?.primaryColors || []}
                                    secondary={kit?.secondaryColors || []}
                                    evidence={kit?.evidence?.primaryColors} suggestion={suggestions.primaryColors}
                                    saving={saving === 'primaryColors'}
                                    onSave={(primary, secondaryColors) => save({ primaryColors: primary, secondaryColors }, 'primaryColors')}
                                />
                            )}
                            {selected === 'toneOfVoice' && (
                                <TextFieldCard
                                    bare title={title('toneOfVoice')} value={kit?.toneOfVoice || ''}
                                    evidence={kit?.evidence?.toneOfVoice} suggestion={suggestions.toneOfVoice}
                                    hint={t('BRAND.TONE_HINT', 'Headlines and captions are written in this voice.')}
                                    multiline rows={4} saving={saving === 'toneOfVoice'}
                                    onSave={(v) => save({ toneOfVoice: v }, 'toneOfVoice')}
                                />
                            )}
                            {selected === 'styleKeywords' && (
                                <ListCard
                                    bare title={title('styleKeywords')} items={kit?.styleKeywords || []}
                                    evidence={kit?.evidence?.styleKeywords} suggestion={suggestions.styleKeywords}
                                    hint={t('BRAND.STYLE_HINT', 'Words the agent writes scenes from — "scandinavian", "warm neutrals", "matte".')}
                                    saving={saving === 'styleKeywords'}
                                    onSave={(v) => save({ styleKeywords: v }, 'styleKeywords')}
                                />
                            )}
                            {selected === 'bannedWords' && (
                                <ListCard
                                    bare title={title('bannedWords')} items={kit?.bannedWords || []}
                                    evidence={kit?.evidence?.bannedWords} suggestion={suggestions.bannedWords}
                                    saving={saving === 'bannedWords'}
                                    onSave={(v) => save({ bannedWords: v }, 'bannedWords')}
                                />
                            )}
                            {selected === 'visualStyle' && kit?.visualStyleStaleAt && (
                                <StaleNotice
                                    busy={looking}
                                    onLook={lookAgain}
                                />
                            )}
                            {selected === 'visualStyle' && (
                                <TextFieldCard
                                    bare title={title('visualStyle')} value={kit?.visualStyle || ''}
                                    evidence={kit?.evidence?.visualStyle}
                                    hint={t('BRAND.VISUAL_STYLE_HINT', 'The interiors, camera, light and mood the agent matches when it composes and judges pictures. Read from your photography.')}
                                    multiline rows={6} saving={saving === 'visualStyle'}
                                    onSave={(v) => save({ visualStyle: v }, 'visualStyle')}
                                />
                            )}
                            {selected === 'referenceImageUrls' && kit?.visualStyleStaleAt && (
                                <StaleNotice busy={looking} onLook={lookAgain} />
                            )}
                            {selected === 'referenceImageUrls' && kit && (
                                <ReferenceImagesCard
                                    bare urls={kit.referenceImageUrls || []} logoUrl={kit.logoUrl}
                                    saving={saving === 'referenceImageUrls'}
                                    onChange={(urls) => save({ referenceImageUrls: urls }, 'referenceImageUrls')}
                                    onUpload={async (files) => {
                                        const uploaded: string[] = [];
                                        for (const f of files) {
                                            const { url } = await api.uploadStyleRef(f);
                                            uploaded.push(url);
                                        }
                                        await save({ referenceImageUrls: [...(kit.referenceImageUrls || []), ...uploaded] }, 'referenceImageUrls');
                                    }}
                                />
                            )}
                            {selected === 'competitors' && (
                                <ListCard
                                    bare title={title('competitors')}
                                    hint={t('BRAND.COMPETITORS_HINT', 'Their websites, separated by commas. Two or more lets us compare.') as string}
                                    items={profile.competitors ?? []}
                                    saving={savingProfile === 'competitors'}
                                    onSave={(items) => void saveProfile({ competitors: items }, 'competitors')}
                                />
                            )}
                            {selected === 'markets' && (
                                <ListCard
                                    bare title={title('markets')}
                                    hint={t('BRAND.MARKETS_HINT', 'Countries or regions you sell in.') as string}
                                    items={profile.markets ?? []}
                                    saving={savingProfile === 'markets'}
                                    onSave={(items) => void saveProfile({ markets: items }, 'markets')}
                                />
                            )}
                            {selected === 'houseRules' && (
                                <TextFieldCard
                                    bare title={title('houseRules')}
                                    hint={t('BRAND.HOUSE_RULES_HINT', 'Plain sentences. "We sell to architects, never to the public." "Always show the product in a real room."') as string}
                                    value={profile.houseRules ?? ''}
                                    multiline rows={5}
                                    saving={savingProfile === 'houseRules'}
                                    onSave={(value) => void saveProfile({ houseRules: value }, 'houseRules')}
                                />
                            )}
                        </div>

                        {/* The kit read back as the agent will read it — under
                            whichever field you are editing, so a change to the
                            words shows up in the sentence it produces. */}
                        {kit && selected !== 'discover' && (
                            <div className="border-t p-5">
                                <HowTheAgentSeesYou kit={kit} />
                            </div>
                        )}
                    </aside>
                )}
            </div>

        </PageContainer>
    );
};

// ═══════════════════════════════════════════════════════════════════
// Cards. Each field is a claim with its evidence, editable in place.
// ═══════════════════════════════════════════════════════════════════

/**
 * A brand colour is data, not theming — it renders from the stored hex.
 * Extracted because the palette shows up in three places on this page.
 */
const Swatch: React.FC<{ hex: string; tooltip?: string; round?: boolean }> = ({ hex, tooltip, round }) => {
    const swatch = (
        <span
            style={{ backgroundColor: hex }}
            className={cn('block border', round ? 'size-[22px] rounded-full' : 'size-7')}
        />
    );
    if (!tooltip) return swatch;
    return (
        <Tooltip>
            <TooltipTrigger asChild>{swatch}</TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
    );
};

const Evidence: React.FC<{ text?: string; suggestion?: any; onTake?: () => void }> = ({ text, suggestion, onTake }) => {
    const { t } = useTranslation();
    if (!text && !suggestion) return null;
    return (
        <div className="mt-3 flex flex-col gap-1.5">
            {text && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <span className="shrink-0 text-muted-foreground">{t('BRAND.WHY', 'Why:')}</span>
                    <span>{text}</span>
                </p>
            )}
            {suggestion && (
                <div className="flex items-center gap-2 bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">
                    <span>
                        {t('BRAND.SUGGESTION', 'Found this time:')}{' '}
                        <strong className="font-medium text-foreground">
                            {Array.isArray(suggestion) ? suggestion.join(', ') : String(suggestion)}
                        </strong>
                    </span>
                    {onTake && (
                        <button
                            type="button"
                            onClick={onTake}
                            className="ms-auto text-xs text-foreground underline"
                        >
                            {t('BRAND.USE_IT', 'Use it')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * The pictures moved; the description of them did not.
 *
 * Shown against the two fields that are actually implicated — the pictures and
 * the prose read from them — rather than as a banner on the page, because a
 * warning away from the thing it is about is a warning nobody connects to
 * anything. The prose is kept while it says this: stale is still better than
 * blank, and deleting it would punish the vendor for adding a reference.
 */
const StaleNotice: React.FC<{ busy?: boolean; onLook: () => void }> = ({ busy, onLook }) => {
    const { t } = useTranslation();
    return (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5">
            <p className="flex-1 text-xs/relaxed text-foreground">
                {t('BRAND.STALE_VISUAL_STYLE', 'Your reference pictures changed, so this no longer describes them.')}
            </p>
            <Button variant="outline" size="sm" disabled={busy} onClick={onLook}>
                {busy
                    ? t('BRAND.LOOKING', 'Looking…')
                    : t('BRAND.LOOK_AGAIN', 'Look again')}
            </Button>
        </div>
    );
};

/**
 * What could not be read, without burying what could.
 *
 * A wall of amber is read as decoration and skipped, which costs the one or
 * two lines in it that were worth acting on. Two are shown; the rest is a
 * sentence away — present, and not competing with the palette above it.
 *
 * Sorted so the actionable ones lead: a line telling the vendor to connect an
 * account is worth more than one about a CSS budget they cannot do anything
 * about.
 */
const VISIBLE_LIMITS = 2;

const Limits: React.FC<{ limits: string[] }> = ({ limits }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const ordered = useMemo(
        () => [...limits].sort((a, b) => Number(/connect/i.test(b)) - Number(/connect/i.test(a))),
        [limits],
    );
    const shown = expanded ? ordered : ordered.slice(0, VISIBLE_LIMITS);
    const hidden = ordered.length - shown.length;

    return (
        <div className="flex flex-col gap-1 text-xs text-warning">
            {shown.map((l) => (
                <span key={l} className="flex items-start gap-1.5">
                    <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                    <span className="leading-relaxed">{l}</span>
                </span>
            ))}
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="self-start text-xs text-muted-foreground underline"
                >
                    {t('BRAND.MORE_LIMITS', { count: hidden, defaultValue: '{{count}} more' })}
                </button>
            )}
        </div>
    );
};

const FieldHeading: React.FC<{ title: string; hint?: string; saving?: boolean }> = ({ title, hint, saving }) => (
    <div className="mb-3">
        <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{title}</p>
            {saving && <Loader2 className="size-3 animate-spin text-muted-foreground" />}
        </div>
        {hint && <p className="mt-0.5 text-xs/relaxed text-muted-foreground">{hint}</p>}
    </div>
);

/**
 * A card, or just its contents.
 *
 * The panel is already a bordered surface, so wrapping each editor in a second
 * one boxes a box. `bare` is the same editor without the chrome.
 */
const FieldShell: React.FC<{ bare?: boolean; className?: string; children: React.ReactNode }> = ({
    bare, className, children,
}) => (bare
    ? <div className={className}>{children}</div>
    : <Card className={className}><CardContent>{children}</CardContent></Card>);

const TextFieldCard: React.FC<{
    title: string; value: string; evidence?: string; suggestion?: any; hint?: string;
    multiline?: boolean; rows?: number; saving?: boolean; bare?: boolean;
    onSave: (value: string) => void;
}> = ({ title, value, evidence, suggestion, hint, multiline, rows, saving, bare, onSave }) => {
    const [draft, setDraft] = useState(value);
    useEffect(() => { setDraft(value); }, [value]);
    const commit = () => { if (draft !== value) onSave(draft); };
    return (
        <FieldShell bare={bare}>
                <FieldHeading title={title} hint={hint} saving={saving} />
                {multiline ? (
                    <Textarea
                        value={draft} rows={rows || 2}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                    />
                ) : (
                    <Input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                    />
                )}
                <Evidence text={evidence} suggestion={suggestion} onTake={suggestion ? () => onSave(String(suggestion)) : undefined} />
        </FieldShell>
    );
};

const ListCard: React.FC<{
    title: string; items: string[]; evidence?: string; suggestion?: any; hint?: string;
    saving?: boolean; bare?: boolean;
    onSave: (items: string[]) => void;
}> = ({ title, items, evidence, suggestion, hint, saving, bare, onSave }) => {
    const [draft, setDraft] = useState(items.join(', '));
    useEffect(() => { setDraft(items.join(', ')); }, [items]);
    const parse = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);
    return (
        <FieldShell bare={bare}>
                <FieldHeading title={title} hint={hint} saving={saving} />
                <Input
                    value={draft} placeholder="one, two, three"
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => { const next = parse(draft); if (next.join('|') !== items.join('|')) onSave(next); }}
                />
                <Evidence text={evidence} suggestion={suggestion} onTake={suggestion ? () => onSave(Array.isArray(suggestion) ? suggestion : parse(String(suggestion))) : undefined} />
        </FieldShell>
    );
};

const ColorsCard: React.FC<{
    title: string; colors: string[]; secondary: string[]; evidence?: string; suggestion?: any;
    saving?: boolean; bare?: boolean;
    onSave: (primary: string[], secondary: string[]) => void;
}> = ({ title, colors, secondary, evidence, suggestion, saving, bare, onSave }) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState(colors.join(', '));
    const [draft2, setDraft2] = useState(secondary.join(', '));
    useEffect(() => { setDraft(colors.join(', ')); }, [colors]);
    useEffect(() => { setDraft2(secondary.join(', ')); }, [secondary]);
    const parse = (v: string) => v.split(',').map((s) => s.trim()).filter((s) => /^#[0-9a-fA-F]{6}$/.test(s));
    const swatches = (list: string[]) => (
        <div className="mb-2 flex flex-wrap gap-1.5">
            {list.map((c) => <Swatch key={c} hex={c} tooltip={c} />)}
        </div>
    );
    return (
        <FieldShell bare={bare}>
                <FieldHeading title={title} hint={t('BRAND.COLOURS_HINT', 'Used for text and graphics over your pictures.') as string} saving={saving} />
                {swatches(colors)}
                <Input
                    value={draft} placeholder="#1b2a49, #c65d3b" className="mb-4"
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => onSave(parse(draft), parse(draft2))}
                />
                <p className="mb-1 text-xs text-muted-foreground">{t('BRAND.SECONDARY', 'Supporting colours')}</p>
                {swatches(secondary)}
                <Input
                    value={draft2} placeholder="#f4efe8"
                    onChange={(e) => setDraft2(e.target.value)}
                    onBlur={() => onSave(parse(draft), parse(draft2))}
                />
                <Evidence text={evidence} suggestion={suggestion} onTake={suggestion ? () => onSave(Array.isArray(suggestion) ? suggestion : parse(String(suggestion)), secondary) : undefined} />
        </FieldShell>
    );
};

const ReferenceImagesCard: React.FC<{
    urls: string[]; logoUrl?: string; saving?: boolean; bare?: boolean;
    onChange: (urls: string[]) => void;
    onUpload: (files: File[]) => Promise<void>;
}> = ({ urls, logoUrl, saving, bare, onChange, onUpload }) => {
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    return (
        <FieldShell bare={bare}>
                <FieldHeading
                    title={t('BRAND.REFERENCES', 'Reference pictures')}
                    hint={t('BRAND.REFERENCES_HINT', 'Photography that looks like you — from your site, your posts, or uploaded here. The agent compares against these when it judges a picture, and reads your visual style from them.') as string}
                    saving={saving || uploading}
                />
                <div className="flex flex-wrap gap-2">
                    {logoUrl && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="flex size-24 items-center justify-center border border-dashed bg-muted p-2">
                                    <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>{t('BRAND.LOGO', 'Your logo')}</TooltipContent>
                        </Tooltip>
                    )}
                    {urls.map((u) => (
                        <div key={u} className="relative size-24 overflow-hidden border">
                            <img src={u} alt="" className="size-full object-cover" />
                            <button
                                type="button"
                                onClick={() => onChange(urls.filter((x) => x !== u))}
                                className="absolute end-0.5 top-0.5 flex size-[18px] items-center justify-center rounded-full bg-foreground/60 text-xs leading-none text-background"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    <Button
                        variant="outline"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="size-24 border-dashed text-muted-foreground"
                    >
                        {uploading ? <Loader2 className="size-4 animate-spin" /> : `+ ${t('BRAND.ADD', 'Add')}`}
                    </Button>
                    <input
                        ref={fileRef} type="file" accept="image/*" multiple hidden
                        onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (!files.length) return;
                            setUploading(true);
                            try { await onUpload(files); } finally { setUploading(false); }
                        }}
                    />
                </div>
        </FieldShell>
    );
};

/**
 * The kit read back as the agent will read it.
 *
 * The vendor should know what the machine believes before it goes and makes
 * things. This is the same text the planner is given, near enough, in prose.
 */
const HowTheAgentSeesYou: React.FC<{ kit: CsBrandKit }> = ({ kit }) => {
    const { t } = useTranslation();
    const parts: string[] = [];
    if (kit.brandName) parts.push(`${kit.brandName}.`);
    if (kit.toneOfVoice) parts.push(t('BRAND.SEES_TONE', { defaultValue: 'They write in a {{tone}} voice.', tone: kit.toneOfVoice }));
    if (kit.styleKeywords?.length) parts.push(t('BRAND.SEES_STYLE', { defaultValue: 'Their world is {{style}}.', style: kit.styleKeywords.join(', ') }));
    if (kit.visualStyle) parts.push(kit.visualStyle);
    if (kit.primaryFont) parts.push(t('BRAND.SEES_FONT', { defaultValue: 'Text on pictures is set in {{font}}.', font: kit.primaryFont }));
    if (kit.bannedWords?.length) parts.push(t('BRAND.SEES_BANNED', { defaultValue: 'They never say {{words}}.', words: kit.bannedWords.join(', ') }));
    return (
        <Card className="bg-muted/50">
            <CardContent>
                <FieldHeading title={t('BRAND.SEES_TITLE', 'How the agent sees you')} hint={t('BRAND.SEES_HINT', 'What the Studio Agent is told about you on every turn.') as string} />
                <p className={cn('text-xs/relaxed', parts.length ? 'text-foreground' : 'text-muted-foreground italic')}>
                    {parts.length ? parts.join(' ') : t('BRAND.SEES_EMPTY', 'Nothing yet. Read your brand above, or fill the fields in, and this is what the agent will work from.')}
                </p>
                {kit.primaryColors?.length ? (
                    <div className="mt-4 flex gap-1.5">
                        {kit.primaryColors.map((c) => <Swatch key={c} hex={c} round />)}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
};

export default BrandPage;
